@if (user) {
  <div
    class="relative mt-auto p-6 py-4 transition-all duration-300 ease-sidebar before:absolute before:top-0 before:h-px before:bg-greyscale-100 before:content-['']"
    [class.before:left-6]="!isCollapsed"
    [class.before:right-6]="!isCollapsed"
    [class.py-6]="isCollapsed"
    [class.px-3]="isCollapsed"
    [class.before:left-3]="isCollapsed"
    [class.before:right-3]="isCollapsed"
    [class.flex]="isCollapsed"
    [class.justify-center]="isCollapsed"
    [class.items-center]="isCollapsed"
  >
    <div
      class="flex min-h-12 cursor-pointer items-center gap-3 rounded-lg transition-all duration-300 ease-out hover:bg-greyscale-100"
      [class.justify-center]="isCollapsed"
      [class.w-12]="isCollapsed"
      [class.h-12]="isCollapsed"
      [class.p-3]="!isCollapsed"
      [class.p-0]="isCollapsed"
      [pTooltip]="
        isCollapsed
          ? user.firstName +
            ' ' +
            user.lastName +
            (user.companyName
              ? ' - ' + user.companyName + ' - ' + translateRole(user.role)
              : ' - ' + translateRole(user.role))
          : ''
      "
      tooltipPosition="right"
      (click)="userMenu.toggle($event)"
      (keydown.enter)="userMenu.toggle($event)"
      (keydown.space)="userMenu.toggle($event); $event.preventDefault()"
      tabindex="0"
      role="button"
      aria-haspopup="true"
    >
      <div class="shrink-0">
        <p-avatar
          [image]="user.company?.logoUrl || undefined"
          [label]="user.company?.logoUrl ? undefined : user.initials"
          size="normal"
          shape="circle"
          [style]="{
            'background-color': 'var(--p-primary-200)',
            color: 'var(--p-primary-700)',
            'font-weight': '600',
            'font-size': '14px',
          }"
        ></p-avatar>
      </div>

      @if (!isCollapsed) {
        <div class="flex-1 overflow-hidden">
          <div
            class="overflow-hidden text-ellipsis whitespace-nowrap font-source-sans text-sm font-semibold text-greyscale-950"
          >
            {{ user.firstName }} {{ user.lastName }}
          </div>
          <div
            class="overflow-hidden text-ellipsis whitespace-nowrap font-source-sans text-xs font-normal text-greyscale-700"
          >
            @if (user.companyName) {
              {{ user.companyName }} - {{ translateRole(user.role) }}
            } @else {
              {{ translateRole(user.role) }}
            }
          </div>
          <div class="flex items-center gap-2 text-xs text-greyscale-700">
            <div>{{ "Pack : " + user.company?.pack?.name }}</div>
          </div>
        </div>
      }

      @if (!isCollapsed) {
        <div
          class="shrink-0 text-greyscale-500 transition-colors duration-300 ease-out hover:text-greyscale-700"
        >
          <lucide-icon name="more-vertical" [size]="16"></lucide-icon>
        </div>
      }
    </div>
  </div>

  <p-popover #userMenu [style]="{ width: '280px' }">
    <div class="p-3">
      <!-- User Info Header -->
      <div class="pb-3">
        <div class="flex items-center gap-3">
          <div class="shrink-0">
            <p-avatar
              [image]="user.company?.logoUrl || undefined"
              [label]="user.company?.logoUrl ? undefined : user.initials"
              size="large"
              shape="circle"
              [style]="{
                'background-color': 'var(--p-primary-200)',
                color: 'var(--p-primary-700)',
                'font-weight': '600',
                'font-size': '16px',
              }"
            ></p-avatar>
          </div>
          <div class="min-w-0 flex-1">
            <div
              class="truncate font-source-sans text-sm font-semibold text-greyscale-950"
            >
              {{ user.firstName }} {{ user.lastName }}
            </div>
            <div class="truncate font-source-sans text-xs text-greyscale-600">
              @if (user.companyName) {
                {{ user.companyName }} - {{ translateRole(user.role) }}
              } @else {
                {{ translateRole(user.role) }}
              }
            </div>
            <div
              class="mt-1 truncate font-source-sans text-xs text-greyscale-500"
            >
              {{ user.email }}
            </div>
          </div>
        </div>
        @if (user.createdAt) {
          <div
            class="mt-2 flex items-center gap-2 font-source-sans text-xs text-greyscale-500"
          >
            <img
              src="/assets/Logo___Greentripper_icon_white.svg"
              alt=""
              class="h-4 w-4 [filter:brightness(0)_invert(58%)_sepia(5%)_hue-rotate(80deg)]"
            />
            {{ "navigation.memberSince" | transloco }}
            {{ formatMemberSince(user.createdAt) }}
          </div>
        }
      </div>

      <!-- Separator -->
      <div class="h-px bg-greyscale-200"></div>

      <!-- Navigation Links -->
      <div class="mt-3 flex flex-col gap-1">
        <button
          class="flex w-full cursor-pointer items-center gap-2 rounded-lg border-0 bg-transparent px-3 py-2 font-source-sans text-sm text-greyscale-700 transition-all hover:bg-greyscale-100"
          (click)="onNavigate('profile'); userMenu.hide()"
          data-testid="menu.profile"
        >
          <lucide-icon
            name="user"
            [size]="16"
            class="text-greyscale-500"
          ></lucide-icon>
          {{ "accountMenu.profile" | transloco }}
        </button>
        <button
          class="flex w-full cursor-pointer items-center gap-2 rounded-lg border-0 bg-transparent px-3 py-2 font-source-sans text-sm text-greyscale-700 transition-all hover:bg-greyscale-100"
          (click)="onNavigate('company'); userMenu.hide()"
          data-testid="menu.company"
        >
          <lucide-icon
            name="building-2"
            [size]="16"
            class="text-greyscale-500"
          ></lucide-icon>
          {{ "accountMenu.company" | transloco }}
        </button>
        <button
          class="flex w-full cursor-pointer items-center gap-2 rounded-lg border-0 bg-transparent px-3 py-2 font-source-sans text-sm text-greyscale-700 transition-all hover:bg-greyscale-100"
          (click)="onNavigate('users'); userMenu.hide()"
          data-testid="menu.users"
        >
          <lucide-icon
            name="users"
            [size]="16"
            class="text-greyscale-500"
          ></lucide-icon>
          {{ "accountMenu.users" | transloco }}
        </button>
        <button
          class="flex w-full cursor-pointer items-center gap-2 rounded-lg border-0 bg-transparent px-3 py-2 font-source-sans text-sm text-greyscale-700 transition-all hover:bg-greyscale-100"
          (click)="onNavigate('billing'); userMenu.hide()"
          data-testid="menu.billing"
        >
          <lucide-icon
            name="credit-card"
            [size]="16"
            class="text-greyscale-500"
          ></lucide-icon>
          {{ "accountMenu.billing" | transloco }}
        </button>
      </div>

      <!-- Separator -->
      <div class="my-2 h-px bg-greyscale-200"></div>

      <!-- Logout -->
      <button
        class="flex w-full cursor-pointer items-center gap-2 rounded-lg border-0 bg-transparent px-3 py-2 font-source-sans text-sm text-error-600 transition-all hover:bg-error-50"
        (click)="onLogoutClick(); userMenu.hide()"
      >
        <lucide-icon name="log-out" [size]="16"></lucide-icon>
        {{ "navigation.logout" | transloco }}
      </button>
    </div>
  </p-popover>
}
